// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import { createdAtField, getCreatedAtSort, idField } from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import RewardAccount from './RewardAccount';
import RewardTransactionBillRecord from './RewardTransactionBillRecord';

class RewardTransaction implements StoredEntity {
    static classInternalName = 'reward_transaction';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        rewardAccountId: {
            displayName: 'Reward Account',
            recordName: 'reward_account_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => RewardAccount,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: true,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        bills: {
            displayName: 'Bills',
            recordName: 'bills',
            dataType: {
                primitive: 'simple-entity',
                isArray: true,
                extraData: {
                    model: () => RewardTransactionBillRecord,
                },
            },
            supportsQuery: false,
            supportsSort: false,
            needToCreate: false,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: true,
        },
        type: {
            displayName: 'Type',
            recordName: 'tx_type',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    1: 'Award',
                    2: 'Redemption',
                    3: 'Adjustment',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        amount: {
            displayName: 'Amount',
            recordName: 'amount',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        spendProgress: {
            displayName: 'Spend Progress',
            recordName: 'spend_progress',
            dataType: {
                primitive: 'amt',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: true,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        description: {
            displayName: 'Description',
            recordName: 'description',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: true,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: true,
        },
    };

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[RewardTransaction.fields.id.recordName];
    }

    get displayName() {
        return this.record[RewardTransaction.fields.id.recordName];
    }

    static endpoints: Endpoints = constructEndpoints('reward-transactions');

    constructor(record: any) {
        this.record = record;
    }
}

export default RewardTransaction;
